import React from 'react';
import { FileText, FileImage, FileSpreadsheet, File, Shield, Building2, Scale, Users, ShoppingBag, Factory, Globe, BookOpen, FolderOpen, Settings, Archive } from 'lucide-react';
import { getToken as _getTokenFromService } from '../../../services/tokenService';

export const API_URL = process.env.REACT_APP_BACKEND_URL;
export const getToken = () => _getTokenFromService() || '';

export const getFileTypeInfo = (filename, mimeType) => {
  const ext = (filename || '').split('.').pop()?.toLowerCase();
  const mime = (mimeType || '').toLowerCase();
  
  if (ext === 'pdf' || mime.includes('pdf')) 
    return { type: 'pdf', icon: FileText, color: 'text-red-600', bg: 'bg-red-50', label: 'PDF', borderColor: 'border-red-200' };
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext) || mime.startsWith('image/'))
    return { type: 'image', icon: FileImage, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Image', borderColor: 'border-blue-200' };
  if (['doc', 'docx'].includes(ext) || mime.includes('word'))
    return { type: 'word', icon: FileText, color: 'text-indigo-600', bg: 'bg-indigo-50', label: 'Word', borderColor: 'border-indigo-200' };
  if (['xls', 'xlsx'].includes(ext) || mime.includes('spreadsheet') || mime.includes('excel'))
    return { type: 'excel', icon: FileSpreadsheet, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Excel', borderColor: 'border-emerald-200' };
  if (['mp4', 'webm', 'avi', 'mov'].includes(ext) || mime.startsWith('video/'))
    return { type: 'video', icon: File, color: 'text-purple-600', bg: 'bg-purple-50', label: 'Video', borderColor: 'border-purple-200' };
  return { type: 'other', icon: File, color: 'text-gray-600', bg: 'bg-gray-50', label: ext?.toUpperCase() || 'Fichier', borderColor: 'border-gray-200' };
};

export const canPreviewInline = (filename, mimeType) => {
  const { type } = getFileTypeInfo(filename, mimeType);
  return ['pdf', 'image'].includes(type);
};

export const getDownloadUrl = (docId) => {
  const token = _getTokenFromService();
  return `${API_URL}/api/ged/download/${docId}?token=${encodeURIComponent(token)}`;
};

export const getViewUrl = (docId) => {
  const token = _getTokenFromService();
  return `${API_URL}/api/ged/download/${docId}?token=${encodeURIComponent(token)}&mode=view`;
};

export const gedDownload = async (docId, filename) => {
  try {
    const token = _getTokenFromService();
    const res = await fetch(`${API_URL}/api/ged/download/${docId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'document';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {
    const url = getDownloadUrl(docId);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'document';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
};

export const GED_ENTITIES = {
  oso: {
    id: 'oso', name: 'OSO', fullName: 'OSO \u2013 Gouvernance & conformit\u00e9',
    badge: 'CENTRALE ESS', badgeColor: 'bg-amber-600', icon: Shield,
    description: 'R\u00f4le strict : gouvernance, normalisation, audit ESS',
    notice: 'OSO ne facture pas, ne per\u00e7oit aucun fonds et n\'\u00e9met aucun re\u00e7u.',
    documents: ['Statuts \u2013 PV AG / CA', 'Charte ESS \u2013 Charte lexicale \u2013 Charte GED', 'Doctrine juridique & notes de conformit\u00e9', 'R\u00e9f\u00e9rentiels mod\u00e8les (devis, re\u00e7us, conventions)', 'Audits \u2013 Rapports d\'impact \u2013 Financeurs', 'Import massif utilisateurs & groupes'],
    categories: [
      { value: 'statuts', label: 'Statuts & PV' }, { value: 'chartes', label: 'Chartes' },
      { value: 'doctrine', label: 'Doctrine juridique' }, { value: 'referentiels', label: 'R\u00e9f\u00e9rentiels mod\u00e8les' },
      { value: 'audits', label: 'Audits & Rapports' }, { value: 'utilisateurs_groupes', label: 'Utilisateurs / Groupes' }
    ]
  },
  fogedom: {
    id: 'fogedom', name: 'FOGEDOM', fullName: 'FOGEDOM \u2013 Fonds & d\u00e9cisions solidaires',
    badge: 'FONDS SOLIDAIRE', badgeColor: 'bg-emerald-600', icon: Building2,
    description: 'Structure d\u00e9di\u00e9e \u00e0 la gestion financi\u00e8re solidaire', notice: null,
    documents: ['PV du Comit\u00e9 de Gestion / Bureau', 'Rapports d\'activit\u00e9 & Comptes annuels', 'Conventions de financement', 'Bordereaux et relev\u00e9s bancaires', 'Devis & Factures \u00e9mises', 'Re\u00e7us de paiement'],
    categories: [
      { value: 'pv_comite', label: 'PV Comit\u00e9' }, { value: 'rapports', label: 'Rapports' },
      { value: 'conventions', label: 'Conventions' }, { value: 'bancaire', label: 'Documents bancaires' },
      { value: 'devis', label: 'Devis' }, { value: 'factures', label: 'Factures' },
      { value: 'devis_recus', label: 'Devis & Re\u00e7us' }, { value: 'recus_ess', label: 'Re\u00e7us ESS' },
      { value: 'comptabilite', label: 'Comptabilit\u00e9' }
    ]
  },
  coppam: {
    id: 'coppam', name: 'COPPAM', fullName: 'COPPAM \u2013 Programme de garanties solidaires',
    badge: 'GARANTIES', badgeColor: 'bg-blue-600', icon: Scale,
    description: 'Souscriptions, attestations, gestion des garanties', notice: null,
    documents: ['Devis ESS / Propositions ESS', 'Attestations RCDS / DOMIUM', 'Contrats cadre', 'Conditions Particuli\u00e8res / G\u00e9n\u00e9rales', 'Documents adh\u00e9sion membre', 'Re\u00e7us ESS \u2013 Contributions solidaires'],
    categories: [
      { value: 'rcds_attestation', label: 'Attestations RCDS' }, { value: 'domium_attestation_provisoire', label: 'Attestations DOMIUM' },
      { value: 'dispositif_souscrit', label: 'Dispositifs souscrits' }, { value: 'adhesions', label: 'Adh\u00e9sions' },
      { value: 'contrats', label: 'Contrats' }, { value: 'recus_ess', label: 'Re\u00e7us ESS' }
    ]
  },
  ftpe: {
    id: 'ftpe', name: 'FTPE', fullName: 'FTPE \u2013 F\u00e9d\u00e9ration des TPE',
    badge: 'F\u00c9D\u00c9RATION', badgeColor: 'bg-orange-600', icon: Users,
    description: 'Adh\u00e9sions, bulletins, cotisations f\u00e9d\u00e9rales', notice: null,
    documents: ['Bulletins d\'adh\u00e9sion', 'Re\u00e7us de cotisation', 'Notes internes / circulaires', 'Rapports f\u00e9d\u00e9raux'],
    categories: [
      { value: 'bulletins', label: 'Bulletins' }, { value: 'cotisations', label: 'Cotisations' },
      { value: 'notes', label: 'Notes internes' }, { value: 'rapports_federaux', label: 'Rapports f\u00e9d\u00e9raux' }
    ]
  },
  kdmarche: {
    id: 'kdmarche', name: 'KDMARCHE', fullName: 'KDMARCH\u00c9 \u2013 Centrale d\'achats solidaire',
    badge: 'ACHATS', badgeColor: 'bg-rose-600', icon: ShoppingBag,
    description: 'R\u00e9f\u00e9rencement fournisseurs, commandes group\u00e9es', notice: null,
    documents: ['Catalogue fournisseurs', 'Bons de commande group\u00e9s', 'Factures fournisseurs', 'Contrats cadre'],
    categories: [
      { value: 'fournisseurs', label: 'Fournisseurs' }, { value: 'commandes', label: 'Commandes' },
      { value: 'factures_fournisseurs', label: 'Factures' }, { value: 'contrats_cadre', label: 'Contrats cadre' }
    ]
  },
  primacop: {
    id: 'primacop', name: 'PRIMACOP', fullName: 'PRIMACOP \u2013 Coop\u00e9rative de production',
    badge: 'PRODUCTION', badgeColor: 'bg-violet-600', icon: Factory,
    description: 'Tra\u00e7abilit\u00e9, certification, production coop\u00e9rative', notice: null,
    documents: ['Fiches de production', 'Certificats qualit\u00e9', 'Documents tra\u00e7abilit\u00e9', 'Rapports de conformit\u00e9'],
    categories: [
      { value: 'production', label: 'Production' }, { value: 'tracabilite', label: 'Tra\u00e7abilit\u00e9' },
      { value: 'certificats', label: 'Certificats' }, { value: 'conformite', label: 'Conformit\u00e9' }
    ]
  }
};

export const ICON_MAP = { Shield, Building2, Scale, Users, ShoppingBag, Factory, FileText, Globe, BookOpen, FolderOpen, Settings, Archive };

export const WORKFLOW_RULES = [
  'Devis -> acceptation -> paiement -> re\u00e7u',
  'Aucun re\u00e7u sans encaissement effectif',
  'Aucune mention fiscale avant paiement',
  'Archivage probant >= 10 ans',
  'Acc\u00e8s par r\u00f4les et journal d\'audit'
];

export const CATEGORY_ENTITY_MAP = {
  'rcds_attestation': 'coppam',
  'domium_attestation_provisoire': 'coppam',
  'domium_attestation_definitive': 'coppam',
  'dispositif_souscrit': 'coppam',
  'adhesions': 'coppam',
  'devis': 'fogedom',
  'devis_recus': 'fogedom',
  'factures': 'fogedom',
  'comptabilite': 'fogedom',
  'statuts': 'oso',
  'chartes': 'oso',
  'photos': 'oso',
  'audits': 'oso',
  'bulletins': 'ftpe',
  'cotisations': 'ftpe',
  'fournisseurs': 'kdmarche',
  'commandes': 'kdmarche',
  'production': 'primacop',
  'tracabilite': 'primacop',
};
